import { useMemo, useState } from 'react'

import { Text, TextButton, TextField, TextWithLabel } from '@joor/design-system'
import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import omit from 'lodash/omit'
import { useSelector } from 'react-redux'

import {
  CustomFeeInput,
  OrderDiscountType,
  OrderPriceModifier,
  OrderPriceModifierType,
  OrderStatus,
} from '__generated__/atlas-types'

import { useRenderToasters } from 'hooks/useRenderToasters'
import {
  isAccountTypeBrand,
  isApplyDiscountsRestricted as isApplyDiscountsRestrictedSelector,
} from 'store/currentUser/selectors'
import { isEditApprovedShippedRestricted as isEditApprovedShippedRestrictedSelector } from 'store/currentUser/selectors'

import { OrderData } from '../../Overview'
import { formatCurrencyPriceRangeStr } from '../ProductsWidget/formatters'
import {
  ConceptText,
  ConceptsContainer,
  CustomFeeWrapper,
  DeleteButton,
  EditModeWrapper,
  StyledCard,
} from './FinancialSummaryWidget.ui'
import { LoadingFinancialSummaryWidget } from './LoadingFinancialSummaryWidget'
import FinancialsViewCard from './components/FinancialsViewCard'
import { useUpdateFinancialSummary } from './financialSummaryWidget.mutations'
import { FinancialSummaryIds } from 'features/Orders/Review/review.ids'
import { NumericFormatInput } from 'features/Orders/components/NumericFormatInput/NumericFormatInput'

export enum FinancialSummaryTestIds {
  TaxNumber = 'financial-summary-tax-number',
  FeesNumber = 'financial-summary-fees-number',
  SubtotalWholesaleNumber = 'financial-subtotal-wholesale-number',
  SubtotalRetailNumber = 'financial-subtotal-retail-number',
  DiscountsNumber = 'financial-summary-discounts-number',
  TotalNumber = 'financial-summary-total-number',
  SaveActionButton = 'financial-summary-save-action-button',
  EditActionButton = 'financial-summary-edit-action-button',
  TotalDueAmount = 'financial-summary-total-due-amount',
  CustomFeeAmount = 'financial-summary-custom-fee-amount',
}

type Props = {
  className?: string
  testId?: string
  id: string
  financials: OrderData['financials']
  brand?: NonNullable<OrderData['brand']>
  status: OrderData['status']
  priceType: OrderData['priceType']
  loading?: boolean
}

type OrderPriceModifierObject = {
  [key: number]: OrderPriceModifier
}

const getInitialFinancials = (financials?: OrderData['financials']) => ({
  retailerDiscount:
    (financials?.orderDiscount?.type === OrderPriceModifierType.NUMERIC
      ? financials?.orderDiscount?.factor
      : financials?.orderDiscount?.percentageAmount) || 0,
  taxes:
    (financials?.tax?.type === OrderPriceModifierType.NUMERIC
      ? financials?.tax?.factor
      : financials?.tax?.percentageAmount) || 0,
  shippingFees:
    (financials?.shippingFees?.type === OrderPriceModifierType.NUMERIC
      ? financials?.shippingFees?.factor
      : financials?.shippingFees?.percentageAmount) || 0,
})

const FinancialSummaryWidget = ({
  id,
  financials,
  brand,
  status,
  priceType,
  className,
  testId,
}: {
  id: string
  financials: OrderData['financials']
  brand?: NonNullable<OrderData['brand']>
  status: OrderData['status']
  priceType: OrderData['priceType']
  className?: string
  testId?: string
}) => {
  const { joorPayUseReach } = useFlags()
  const isBrand = useSelector(isAccountTypeBrand)
  const isApplyDiscountsRestricted = useSelector(
    isApplyDiscountsRestrictedSelector,
  )
  const isEditApprovedShippedRestricted = useSelector(
    isEditApprovedShippedRestrictedSelector,
  )
  const [isEditMode, setIsEditMode] = useState(false)
  const [priceChanges, setPriceChanges] = useState(
    getInitialFinancials(financials),
  )
  const [retailerDiscountFormat, setRetailerDiscountFormat] = useState(
    financials?.orderDiscount?.type || OrderPriceModifierType.NUMERIC,
  )
  const [taxFormat, setTaxFormat] = useState(
    financials?.tax?.type || OrderPriceModifierType.NUMERIC,
  )

  const customFeesData = useMemo(
    () =>
      financials?.customFees
        ? financials?.customFees.reduce((acc, item, index) => {
            acc[index] = item
            return acc
          }, {} as OrderPriceModifierObject)
        : {},
    [financials],
  )

  const [customFees, setCustomFees] = useState(customFeesData)
  const { renderSuccessToast, renderErrorToast } = useRenderToasters()
  const { updateFinancialSummary, loading: saveLoading } =
    useUpdateFinancialSummary()
  const customFeesArray = Object.values(customFees)

  if (saveLoading) {
    return (
      <StyledCard
        className={className}
        testId={testId}
        title="Financial Summary"
      >
        <LoadingFinancialSummaryWidget />
      </StyledCard>
    )
  }

  const restrictEditByOrderStatus =
    (status === OrderStatus.APPROVED || status === OrderStatus.SHIPPED) &&
    isEditApprovedShippedRestricted

  const canEdit =
    isBrand && !restrictEditByOrderStatus && status !== OrderStatus.CANCELLED

  const currencyCode = priceType?.currency?.code ?? ''
  const retailCurrencyCode = priceType?.retailCurrency?.code ?? ''

  const canEditDiscount =
    brand?.orderSettings?.allowOrderDiscounts && !isApplyDiscountsRestricted
  const canViewDiscount =
    financials?.orderDiscount?.factor !== '0.00' ||
    brand?.orderSettings?.allowOrderDiscounts

  const canViewGatedFields = isBrand && !joorPayUseReach

  const onFieldChange = (evt: { target: HTMLInputElement }, field: string) => {
    const value = evt?.target?.value
    setPriceChanges({ ...priceChanges, [field]: value })
  }

  const handleCardBtnAction = async () => {
    if (isEditMode) {
      try {
        const shouldUpdateOrderDiscount =
          retailerDiscountFormat !== financials?.orderDiscount?.type ||
          priceChanges.retailerDiscount !==
            (retailerDiscountFormat === OrderPriceModifierType.NUMERIC
              ? financials?.orderDiscount?.factor
              : financials?.orderDiscount?.percentageAmount)

        const shouldUpdateOrderTaxes =
          taxFormat !== financials?.tax?.type ||
          priceChanges.taxes !==
            (taxFormat === OrderPriceModifierType.NUMERIC
              ? financials?.tax.factor
              : financials?.tax.percentageAmount)

        const shouldUpdateShippingFees =
          Number(priceChanges.shippingFees) !==
          Number(financials?.shippingFees?.factor)

        const customFeesChanged =
          customFeesArray.length !== financials?.customFees?.length ||
          financials?.customFees?.some(
            (customFee, index) =>
              customFeesArray[index].factor !== customFee.factor ||
              customFeesArray[index].percentageAmount !==
                customFee.percentageAmount ||
              customFeesArray[index].name !== customFee.name ||
              customFeesArray[index].type !== customFee.type,
          )
        const shouldUpdateCustomFees =
          customFeesArray.some(
            (customFee) => customFee.name && !customFee.id,
          ) || customFeesChanged

        const customFeesMutationParam = customFeesArray
          .filter(
            (customFee) => customFee.id || (!customFee.id && customFee.name),
          )
          .map((customFee) => {
            return {
              ...(customFee.id && { id: customFee.id }),
              name: customFee.id
                ? customFee.name || 'Custom Fee'
                : customFee.name,
              factor:
                customFee.type === OrderPriceModifierType.NUMERIC
                  ? customFee.factor
                  : customFee.percentageAmount,
              type: customFee.type,
            }
          })
        if (
          shouldUpdateOrderDiscount ||
          shouldUpdateOrderTaxes ||
          shouldUpdateShippingFees ||
          shouldUpdateCustomFees
        ) {
          const response = await updateFinancialSummary({
            orderId: id || '',
            ...(shouldUpdateOrderDiscount
              ? {
                  discount: {
                    factor: priceChanges.retailerDiscount || 0,
                    type:
                      // This conversion is needed since the types between the mutation and the query are different
                      retailerDiscountFormat === OrderPriceModifierType.NUMERIC
                        ? OrderDiscountType.NUMERIC
                        : OrderDiscountType.PERCENT,
                  },
                }
              : {}),
            ...(shouldUpdateOrderTaxes
              ? {
                  tax: {
                    modifierFactor:
                      (typeof priceChanges.taxes === 'string'
                        ? Number(priceChanges.taxes)
                        : priceChanges.taxes) || 0,
                    type: taxFormat,
                  },
                }
              : {}),
            ...(shouldUpdateShippingFees
              ? {
                  shippingPrice:
                    (typeof priceChanges.shippingFees === 'string'
                      ? Number(priceChanges.shippingFees)
                      : priceChanges.shippingFees) || 0,
                }
              : {}),
            ...(shouldUpdateCustomFees
              ? { customFees: customFeesMutationParam as CustomFeeInput[] }
              : {}),
          })
          if (
            (response.errors && response.errors.length > 0) ||
            (response?.data?.updateOrders?.errors &&
              response?.data?.updateOrders.errors.length > 0)
          ) {
            throw new Error()
          }
          if (response.data) {
            renderSuccessToast({
              title: 'Financial Summary updated',
              description: 'Financial Summary has been successfully updated',
            })
          }
        }
      } catch (error) {
        renderErrorToast({
          title: 'Unable to update Financial Summary',
          description: 'An unexpected server error has occurred',
        })
      }
    }
    setIsEditMode((prevState) => !prevState)
    setPriceChanges(getInitialFinancials(financials))
    setCustomFees(
      financials?.customFees
        ? financials?.customFees.reduce((acc, item, index) => {
            acc[index] = item
            return acc
          }, {} as OrderPriceModifierObject)
        : {},
    )
  }

  const handleAddCustomFee = () => {
    const customFeesIndexes = Object.keys(customFees)
    const newKey =
      customFeesIndexes.length > 0
        ? parseInt(customFeesIndexes.pop() || '-1') + 1
        : 0
    setCustomFees((prevState) => ({
      ...prevState,
      [newKey]: {
        name: '',
        type: OrderPriceModifierType.NUMERIC,
        percentageAmount: '0',
        factor: '0',
      },
    }))
  }

  const handleCustomFeeNameChange = ({
    newValue,
    index,
  }: {
    newValue: string
    index: number
  }) => {
    const editedFee = { ...customFees[index] }
    editedFee.name = newValue
    setCustomFees((prevState) => ({
      ...prevState,
      [index]: editedFee,
    }))
  }

  const handleCustomFeeTypeChange = ({
    newFormat,
    index,
  }: {
    newFormat: OrderPriceModifierType
    index: number
  }) => {
    const editedFee = { ...customFees[index] }
    editedFee.type = newFormat
    editedFee.percentageAmount = '0'
    editedFee.factor = '0'
    setCustomFees((prevState) => ({
      ...prevState,
      [index]: editedFee,
    }))
  }

  const handleCustomFeeNumberChange = ({
    newNumber,
    index,
  }: {
    newNumber: string
    index: number
  }) => {
    const editedFee = { ...customFees[index] }
    if (editedFee.type === OrderPriceModifierType.PERCENT) {
      editedFee.percentageAmount = newNumber
    } else {
      editedFee.factor = newNumber
    }

    setCustomFees((prevState) => ({
      ...prevState,
      [index]: editedFee,
    }))
  }

  return isEditMode ? (
    <StyledCard
      className={className}
      testId={testId}
      title="Financial Summary"
      actionButtonTestId={FinancialSummaryTestIds.SaveActionButton}
      actionButtonLabel="Save"
      onClickActionButton={handleCardBtnAction}
      actionButtonId={FinancialSummaryIds.SaveButtonId}
    >
      <ConceptsContainer>
        <EditModeWrapper>
          <TextWithLabel
            label="Total Order Value"
            caption="Order values are based on entered product information and cannot be edited directly."
          >
            <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
              {formatCurrencyPriceRangeStr(
                currencyCode,
                financials?.wholesaleTotal,
              )}
            </Text>
          </TextWithLabel>
          <TextWithLabel
            label="Total Retail Value"
            caption="Total Retail Value is based entered product information cannot be edited directly."
          >
            <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
              {formatCurrencyPriceRangeStr(
                retailCurrencyCode,
                financials?.suggestedRetailTotal,
              )}
            </Text>
          </TextWithLabel>
          <div>
            <hr />
          </div>
          <TextWithLabel
            label="Subtotal Wholesale Incl. Product Discounts"
            caption="Subtotal Wholesale is a calculation cannot be edited directly."
          >
            <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
              {formatCurrencyPriceRangeStr(currencyCode, financials?.subTotal)}
            </Text>
          </TextWithLabel>
          {canEditDiscount ? (
            <NumericFormatInput
              title="Order Discount"
              value={priceChanges.retailerDiscount}
              handleOnBlur={(e: { target: HTMLInputElement }) =>
                onFieldChange(e, 'retailerDiscount')
              }
              selectedFormatType={retailerDiscountFormat}
              setSelectedFormatType={(format) => {
                setRetailerDiscountFormat(format)
                setPriceChanges({ ...priceChanges, retailerDiscount: 0 })
              }}
              maxAmount={financials?.wholesaleTotal?.minPrice}
              valueFormatId={FinancialSummaryIds.OrderDiscountValueId}
              percentFormatId={FinancialSummaryIds.OrderDiscountPercentId}
            />
          ) : (
            <TextWithLabel
              label="Order Discount"
              caption={'You are restricted from editing this field'}
            >
              <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                {`${financials?.orderDiscount?.percentageAmount} 
          % (${currencyCode} -${financials?.orderDiscount?.factor})`}
              </Text>
            </TextWithLabel>
          )}
          {joorPayUseReach && (
            <NumericFormatInput
              id={FinancialSummaryTestIds.TaxNumber}
              title="Taxes"
              value={priceChanges.taxes}
              handleOnBlur={(e: { target: HTMLInputElement }) =>
                onFieldChange(e, 'taxes')
              }
              selectedFormatType={taxFormat}
              setSelectedFormatType={(format) => {
                setTaxFormat(format)
                setPriceChanges({ ...priceChanges, taxes: 0 })
              }}
              valueFormatId={FinancialSummaryIds.TaxValueId}
              percentFormatId={FinancialSummaryIds.TaxPercentId}
            />
          )}
          <NumericFormatInput
            title="Shipping Fees"
            value={priceChanges.shippingFees}
            handleOnBlur={(e: { target: HTMLInputElement }) =>
              onFieldChange(e, 'shippingFees')
            }
            id={FinancialSummaryTestIds.FeesNumber}
          />
          <hr />
          <TextWithLabel
            label="Grand Total"
            caption="Grand Total is a calculation cannot be edited directly."
          >
            <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
              {formatCurrencyPriceRangeStr(
                currencyCode,
                financials?.grandTotal,
              )}
            </Text>
          </TextWithLabel>
          {joorPayUseReach && (
            <>
              {customFeesArray.length < 3 && (
                <TextButton
                  iconName="add"
                  onClick={handleAddCustomFee}
                  id={FinancialSummaryIds.AddCustomFeesButtonId}
                >
                  Add Custom Fee
                </TextButton>
              )}
              {customFeesArray.length > 0 &&
                customFeesArray.map((_, index) => (
                  <CustomFeeWrapper>
                    <DeleteButton
                      iconName="trash"
                      data-testid="delete-custom-fee-btn"
                      onClick={() => {
                        const customFeesWithoutDeleted = omit(customFees, index)
                        const newCustomFeesObj = Object.assign(
                          {},
                          Object.values(customFeesWithoutDeleted),
                        ) as OrderPriceModifierObject
                        setCustomFees(newCustomFeesObj)
                      }}
                    >
                      Delete
                    </DeleteButton>
                    <TextField
                      label="Fee Name"
                      onChange={({ target: { value: newValue } }) => {
                        handleCustomFeeNameChange({ newValue, index })
                      }}
                      value={customFees[index].name || ''}
                    />
                    <NumericFormatInput
                      title="Fee Amount"
                      selectedFormatType={customFees[index].type!}
                      setSelectedFormatType={(newFormat) =>
                        handleCustomFeeTypeChange({ newFormat, index })
                      }
                      value={
                        customFees[index].type ===
                        OrderPriceModifierType.NUMERIC
                          ? customFees[index].factor!
                          : customFees[index].percentageAmount!
                      }
                      handleOnBlur={(e: { target: { value: string } }) =>
                        handleCustomFeeNumberChange({
                          newNumber: e.target.value,
                          index,
                        })
                      }
                      valueFormatId={FinancialSummaryIds.OrderCustomFeeValueId}
                      percentFormatId={
                        FinancialSummaryIds.OrderCustomFeePercentId
                      }
                    />
                    <ConceptText className="light small">
                      Charging additional fees may be prohibited by law in
                      certain jurisdictions. It is your responsibility to act in
                      accordance with applicable law.
                    </ConceptText>
                    <hr />
                  </CustomFeeWrapper>
                ))}
              {customFeesArray.length > 0 && (
                <TextWithLabel
                  label="Total Due"
                  caption="Total Due is a calculation and cannot be edited directly."
                >
                  <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                    {formatCurrencyPriceRangeStr(
                      currencyCode,
                      financials?.dueTotal,
                    )}
                  </Text>
                </TextWithLabel>
              )}
            </>
          )}
        </EditModeWrapper>
      </ConceptsContainer>
    </StyledCard>
  ) : (
    <FinancialsViewCard
      canEdit={canEdit}
      canViewDiscount={!!canViewDiscount}
      canViewJoorPayPriceModifiers={joorPayUseReach}
      currencyCode={currencyCode}
      retailCurrencyCode={retailCurrencyCode}
      suggestedRetailTotal={financials?.suggestedRetailTotal}
      wholesaleTotal={financials?.wholesaleTotal}
      subTotal={financials?.subTotal}
      grandTotal={financials?.grandTotal}
      dueTotal={financials?.dueTotal}
      orderDiscount={financials?.orderDiscount}
      shippingFees={financials?.shippingFees}
      tax={financials?.tax}
      customFees={financials?.customFees}
      shippingRates={brand?.orderSettings?.shippingRates}
      testId={testId}
      onClickActionButton={() => {
        setIsEditMode((prevState) => !prevState)
        setPriceChanges(getInitialFinancials(financials))
        setCustomFees(
          financials?.customFees
            ? financials?.customFees.reduce((acc, item, index) => {
                acc[index] = item
                return acc
              }, {} as OrderPriceModifierObject)
            : {},
        )
      }}
      canViewGatedFields={canViewGatedFields}
    />
  )
}

export const FinancialSummaryContainer = ({
  className = '',
  testId,
  id,
  financials,
  brand,
  status,
  priceType,
  loading,
}: Props) => {
  if (loading || !id) {
    return (
      <StyledCard
        className={className}
        testId={testId}
        title="Financial Summary"
      >
        <LoadingFinancialSummaryWidget />
      </StyledCard>
    )
  }

  return (
    <FinancialSummaryWidget
      id={id}
      brand={brand}
      financials={financials}
      status={status}
      priceType={priceType}
      testId={testId}
    />
  )
}
